import { ArrowUpRight } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';

// ═══════════════════════════════════════════════════════════
// PoweredByIntralys — v2 — pill "Création fièrement québécoise signée INTRALYS"
// Cliquable -> https://intralys.net/. Inspiré roynakhal.com signature.
// Composants : SVG inline 3 barres Intralys + wording + icone external link.
// Placement : coin bas-droit des calculateurs + sections API live + footer global.
// Style : pill discret backdrop-blur, hover lift + couleurs Intralys (palette
// Océanne terracotta via --primary, fallback arbitrary value).
// Bilingue FR/EN via useLang() (pattern Océanne — ternaire inline).
// ═══════════════════════════════════════════════════════════

type Props = {
  className?: string;
  /** Variant pour Footer global : plus subtil, sans pill background. */
  variant?: 'pill' | 'inline';
};

// Logo 3 barres Intralys (orange + bleu clair + bleu vif) — SVG inline portable.
function IntralysMark({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 30 22"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="0" y="11" width="7" height="11" rx="0.5" fill="#F5A02C" />
      <rect x="11" y="5" width="7" height="17" rx="0.5" fill="#BDDFEF" />
      <rect x="22" y="0" width="7" height="22" rx="0.5" fill="#26A6E8" />
    </svg>
  );
}

export function PoweredByIntralys({ className = '', variant = 'pill' }: Props) {
  const { lang } = useLang();
  const text =
    lang === 'fr' ? 'Création fièrement québécoise signée' : 'Proudly Quebec creation by';
  // Phase 27.79-sextus a11y — WCAG 2.5.3 (Label in Name) : aria-label
  // retiré entièrement. Le visible text "Création fièrement québécoise signée
  // INTRALYS" sert d'accessible name + les 2 SVG sont aria-hidden. L'info
  // "external link" est conventionnelle via target="_blank" + icon ArrowUpRight.

  if (variant === 'inline') {
    // Footer global : ligne inline minimaliste, sans background pill.
    return (
      <div className={`flex items-center justify-center gap-2 ${className}`}>
        <a
          href="https://intralys.net/"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-[color:var(--primary)]"
        >
          <span>{text}</span>
          <IntralysMark className="h-3 w-4" />
          <span className="font-bold tracking-[0.2em] text-foreground">INTRALYS</span>
          <ArrowUpRight
            className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            strokeWidth={2}
            aria-hidden="true"
          />
        </a>
      </div>
    );
  }

  // Pill (défaut) : coin bas-droit calculateurs + API live.
  return (
    <div className={`mt-4 flex justify-end ${className}`}>
      <a
        href="https://intralys.net/"
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex items-center gap-2 rounded-full border border-[color:var(--primary)]/20 bg-background/60 px-3 py-1.5 font-sans text-[10px] uppercase tracking-[0.2em] text-muted-foreground backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[color:var(--primary)]/50 hover:bg-background/90 hover:text-[color:var(--primary)]"
      >
        <span>{text}</span>
        <IntralysMark className="h-3 w-4 shrink-0" />
        <span className="font-bold tracking-[0.18em] text-foreground">INTRALYS</span>
        <ArrowUpRight
          className="h-3 w-3 text-[color:var(--primary)]/70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          strokeWidth={2}
        />
      </a>
    </div>
  );
}
